import React, { useState, useCallback } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { get } from 'lodash'

import { OrderCoreSchemaType } from '../orderCore.schema'
import { FormGrid, FormGroup, Separator } from '../styles/shared'
import { Label, Input, Textarea, SwitchBox, SwitchGroup, SwitchInput, SwitchSlider } from './Details.style'
import InfiniteSelect from '../../../../components/InfiniteSelect'
import ApiService from '../../../../services/ApiService'
import CollapsibleSectionHeader from '../components/CollapsibleSectionHeader'

interface DetailsProps {
  attachmentsSlot?: React.ReactNode
  productionObservationSlot?: React.ReactNode
}

const Details: React.FC<DetailsProps> = ({ attachmentsSlot, productionObservationSlot }) => {
  const [isOpen, setIsOpen] = useState(true)
  const { control, register, setValue, watch, formState: { errors } } = useFormContext<OrderCoreSchemaType>()

  const fetchOrderTypes = useCallback(async (limit: number, offset: number, search?: string) => {
    const page = Math.floor(offset / limit) + 1
    const response = await ApiService.steelOrders.getOrderTypes({ page, limit, search })
    return Array.isArray(response.data) ? response.data : (response.data?.data ?? [])
  }, [])

  const createOrderTypeOption = useCallback(async (name: string) => {
    const response = await ApiService.steelOrders.createOrderType({ name })
    return response.data
  }, [])

  const deleteOrderTypeOption = useCallback(async (id: any) => {
    await ApiService.steelOrders.deleteOrderType(Number(id))
  }, [])

  const fetchRepresentatives = useCallback(async (limit: number, offset: number, search?: string) => {
    const page = Math.floor(offset / limit) + 1
    const response = await ApiService.steelOrders.getRepresentatives({ page, limit, search })
    return Array.isArray(response.data) ? response.data : (response.data?.data ?? [])
  }, [])

  const createRepresentativeOption = useCallback(async (name: string) => {
    const response = await ApiService.steelOrders.createRepresentative({ name })
    return response.data
  }, [])

  const deleteRepresentativeOption = useCallback(async (id: any) => {
    await ApiService.steelOrders.deleteRepresentative(Number(id))
  }, [])

  const fetchOrderSituations = useCallback(async (limit: number, offset: number, search?: string) => {
    const page = Math.floor(offset / limit) + 1
    const response = await ApiService.steelOrders.getOrderSituations({ page, limit, search })
    return Array.isArray(response.data) ? response.data : (response.data?.data ?? [])
  }, [])

  const createOrderSituationOption = useCallback(async (name: string) => {
    const response = await ApiService.steelOrders.createOrderSituation({ name })
    return response.data
  }, [])

  const deleteOrderSituationOption = useCallback(async (id: any) => {
    await ApiService.steelOrders.deleteOrderSituation(Number(id))
  }, [])

  const fetchGeneralClasses = useCallback(async (limit: number, offset: number, search?: string) => {
    const page = Math.floor(offset / limit) + 1
    const response = await ApiService.steelOrders.getGeneralClasses({ page, limit, search })
    return Array.isArray(response.data) ? response.data : (response.data?.data ?? [])
  }, [])

  const createGeneralClassOption = useCallback(async (name: string) => {
    const response = await ApiService.steelOrders.createGeneralClass({ name })
    return response.data
  }, [])

  const deleteGeneralClassOption = useCallback(async (id: any) => {
    await ApiService.steelOrders.deleteGeneralClass(Number(id))
  }, [])

  const orderTypeId = watch('orderTypeId')
  const orderTypeName = watch('orderType')
  const selectedOrderType = orderTypeId ? { id: orderTypeId, name: String(orderTypeName ?? '') } : null

  const representativeId = watch('representativeId')
  const representativeName = watch('representativeName')
  const selectedRepresentative = representativeId ? { id: representativeId, name: String(representativeName ?? '') } : null

  const orderSituationId = watch('orderSituationId')
  const orderSituationName = watch('orderSituationName')
  const selectedOrderSituation = orderSituationId ? { id: orderSituationId, name: String(orderSituationName ?? '') } : null

  const hasCriticalAnalysis = !!watch('criticalAnalysis')

  const generalClassId = watch('generalClassId')
  const generalClassName = watch('generalClassName')
  const selectedGeneralClass = generalClassId ? { id: generalClassId, name: String(generalClassName ?? '') } : null

  const handleSelectOrderType = useCallback((option: any) => {
    setValue('orderTypeId', option?.id, { shouldDirty: true })
    setValue('orderType', option?.name ?? '', { shouldDirty: true })
  }, [setValue])

  const handleSelectRepresentative = useCallback((option: any) => {
    setValue('representativeId', option?.id, { shouldDirty: true })
    setValue('representativeName', option?.name ?? '', { shouldDirty: true })
  }, [setValue])

  const handleSelectOrderSituation = useCallback((option: any) => {
    setValue('orderSituationId', option?.id, { shouldDirty: true })
    setValue('orderSituationName', option?.name ?? '', { shouldDirty: true })
  }, [setValue])

  const handleSelectGeneralClass = useCallback((option: any) => {
    setValue('generalClassId', option?.id, { shouldDirty: true })
    setValue('generalClassName', option?.name ?? '', { shouldDirty: true })
  }, [setValue])

  return (
    <>
      <CollapsibleSectionHeader
        title="Detalhes do pedido"
        isOpen={isOpen}
        onToggle={() => setIsOpen((v) => !v)}
      />

      {isOpen && (
        <>
          <FormGrid columns={3}>
            <FormGroup>
              <InfiniteSelect
                mode="crud"
                allowDelete={true}
                getOptionLabel={(opt: any) => opt.name}
                label="Tipo de Pedido"
                value={selectedOrderType}
                fetchFunction={fetchOrderTypes}
                onItemCreate={createOrderTypeOption}
                onItemDelete={deleteOrderTypeOption}
                onSelect={handleSelectOrderType}
                hasError={!!get(errors, 'orderTypeId')}
              />
            </FormGroup>

            <FormGroup>
              <InfiniteSelect
                mode="crud"
                allowDelete={true}
                getOptionLabel={(opt: any) => opt.name}
                label="Representante"
                value={selectedRepresentative}
                fetchFunction={fetchRepresentatives}
                onItemCreate={createRepresentativeOption}
                onItemDelete={deleteRepresentativeOption}
                onSelect={handleSelectRepresentative}
                hasError={!!get(errors, 'representativeId')}
              />
            </FormGroup>

            <FormGroup>
              <InfiniteSelect
                mode="crud"
                allowDelete={true}
                getOptionLabel={(opt: any) => opt.name}
                label="Situação do Pedido"
                value={selectedOrderSituation}
                fetchFunction={fetchOrderSituations}
                onItemCreate={createOrderSituationOption}
                onItemDelete={deleteOrderSituationOption}
                onSelect={handleSelectOrderSituation}
                hasError={!!get(errors, 'orderSituationId')}
              />
            </FormGroup>
          </FormGrid>

          <FormGrid columns={3}>
            <FormGroup>
              <InfiniteSelect
                mode="crud"
                allowDelete={true}
                getOptionLabel={(opt: any) => opt.name}
                label="Classe Geral"
                value={selectedGeneralClass}
                fetchFunction={fetchGeneralClasses}
                onItemCreate={createGeneralClassOption}
                onItemDelete={deleteGeneralClassOption}
                onSelect={handleSelectGeneralClass}
                hasError={!!get(errors, 'generalClassId')}
              />
            </FormGroup>

            <FormGroup>
              <Label>Informação extra</Label>
              <Controller
                name="extraInfo"
                control={control}
                render={({ field }) => (
                  <Input {...field} value={field.value ?? ''} placeholder="Digite" />
                )}
              />
            </FormGroup>

            {productionObservationSlot}
          </FormGrid>

          <FormGrid columns={3}>
            <SwitchGroup>
              <Label htmlFor="criticalAnalysis">Análise Crítica</Label>
              <Controller
                name="criticalAnalysis"
                control={control}
                render={({ field }) => (
                  <SwitchBox>
                    <SwitchInput
                      type="checkbox"
                      id="criticalAnalysis"
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                    <SwitchSlider className="slider"></SwitchSlider>
                  </SwitchBox>
                )}
              />
            </SwitchGroup>

            {hasCriticalAnalysis && (
              <FormGroup>
                <Label>Descrição da Análise</Label>
                <Textarea
                  {...register('criticalAnalysisDesc')}
                  placeholder="Digite a descrição da análise"
                />
              </FormGroup>
            )}

            {attachmentsSlot}
          </FormGrid>
        </>
      )}
      <Separator />
    </>
  )
}

export default Details
